import type { ControlListItem } from "@/models/control.model";
import { showAlertCallout } from "@/shared/alerts";
import { useControlRepository } from "@/shared/contexts/repository-context";
import { useRequestReport } from "@/spz-api/analysis/analysis";
import { getGetControlListQueryKey } from "@/spz-api/controls/controls";
import { useGetMe } from "@/spz-api/user/user";
import { datadogRum } from "@datadog/browser-rum";
import {
  ANALYSIS_REPORT_REQUEST_DENIED,
  ANALYSIS_REPORT_REQUEST_ERROR,
  ANALYSIS_REQUEST_REGISTERED,
  HARD_LIMITS,
} from "@spz/config";
import { useQuery } from "@tanstack/react-query";
import type { Table } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getTotalResultCount } from "../utils/results-counter";

const VALIDATION_DELAY_MS = 2000;

type ExportStatus = "idle" | "validating" | "error";

type UseExportExcelPopoverViewModelParams = {
  analysisId: string;
  table: Table<ControlListItem>;
  enableDataSourceOption?: boolean;
  onClose: () => void;
};

export function useExportExcelPopoverViewModel({
  analysisId,
  table,
  enableDataSourceOption,
  onClose,
}: UseExportExcelPopoverViewModelParams) {
  const { t } = useTranslation();
  const controlRepository = useControlRepository();
  const [exportStatus, setExportStatus] = useState<ExportStatus>("idle");
  const { data: me } = useGetMe();

  // When no rows are selected, we export all controls
  const hasNoSelection = table.getSelectedRowModel().flatRows.length === 0;

  const allControls = useQuery({
    queryKey: getGetControlListQueryKey({ analysisId }),
    queryFn: () => controlRepository.getControlsAndTags({ analysisId }),
    enabled: hasNoSelection,
  }).data?.controls;

  const baseDatadogAttributes = {
    userId: me?.id,
    orgId: me?.organization.id,
    sourceDataIncluded: enableDataSourceOption,
  };

  const mutation = useRequestReport({
    mutation: {
      onSuccess: () => {
        onClose();
        showAlertCallout({
          type: "success",
          title: t("generate_report_toast_start_title"),
          description: t("generate_report_toast_start_description"),
        });
      },
      onError: (error: unknown) => {
        onClose();
        showAlertCallout({
          type: "critical",
          title: t("generic_error"),
          description: t("export_excel_generic_error"),
        });
        // send datadog action
        datadogRum.addAction(ANALYSIS_REPORT_REQUEST_ERROR, {
          ...baseDatadogAttributes,
          error: JSON.stringify(error),
        });
      },
    },
  });

  const handleCancel = () => {
    onClose();
    showAlertCallout({
      type: "critical",
      title: t("generic_error"),
      description: t("export_excel_generic_error"),
    });
    setExportStatus("idle");
  };

  const handleGenerate = async () => {
    // Calculate selection data only if we have selected rows
    let controlIdsToExport: string[];
    let totalToValidate: number;

    if (hasNoSelection) {
      controlIdsToExport = []; // empty array means all controls
      totalToValidate = getTotalResultCount(allControls ?? []) || 0;
    } else {
      const selectedRowModel = table.getSelectedRowModel();
      const selectedRows = selectedRowModel.flatRows;
      controlIdsToExport = selectedRows.map((row) => row.id);
      totalToValidate = getTotalResultCount(
        selectedRows.map((row) => ({
          resultCount: Number(row.original.resultCount),
        })),
      );
    }

    const commonDatadogAttributes = {
      ...baseDatadogAttributes,
      numberOfResults: totalToValidate,
      resultLimit: HARD_LIMITS.reports.controlResultSelectLimit,
      // empty array if all controls are exported
      controlIds: hasNoSelection ? [] : controlIdsToExport,
    };

    // Start validation
    setExportStatus("validating");

    // 2-second fake loader
    await new Promise((resolve) => setTimeout(resolve, VALIDATION_DELAY_MS));

    // Validate against limit
    if (totalToValidate > HARD_LIMITS.reports.controlResultSelectLimit) {
      setExportStatus("error");
      datadogRum.addAction(ANALYSIS_REPORT_REQUEST_DENIED, {
        ...commonDatadogAttributes,
        reason: "result_count_exceeded_limit",
      });
      return;
    }

    // Proceed with API call
    datadogRum.addAction(ANALYSIS_REQUEST_REGISTERED, {
      ...commonDatadogAttributes,
    });
    mutation.mutate({
      data: { controlIds: controlIdsToExport },
      id: analysisId,
    });
  };

  // If nothing is selected, we'll export all controls, so show that count
  const selectedRowsCount = hasNoSelection
    ? allControls?.length
    : table.getSelectedRowModel().flatRows.length;

  return {
    t,
    exportStatus,
    controlsSize: selectedRowsCount ?? 0,
    handleGenerate,
    handleCancel,
  };
}
